import React, {
  forwardRef,
  useRef,
  useImperativeHandle,
  useCallback,
  useEffect,
  useState,
  useMemo,
} from 'react';
import {
  TablePaginatedV8,
  order2Sorting,
  TableCellV8,
  EditCellV8,
  CellDateV8,
} from 'motion-components';
import { generatePath, useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';

import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import { gridInactiveRowStyle, gridRowSelectedStyle } from 'shared-pages/common/grid-helpfunc';
import { getRouteByName } from 'app/routes';
import DeleteCellV8 from 'shared-pages/components/buttons/delete-cell-v8';
import { formatFloat } from 'shared-pages/utils/helpers';

/* ----------- Grid ------------------ */

export const GridPage = forwardRef((props: any, ref) => {
  const {
    data: inData,
    dataCount,
    initPageIndex,
    onPageChange,
    loading,
    handleExport,
    initOrderBy,
    onOrderChange,
    selected,
    setSelected,
    refetch,
    isGridInForm,
  } = props;
  const location = useLocation<any>();
  const history = useHistory();
  const { appStore, tableStore } = useRootStore();
  const { t, i18n } = useTranslation();

  const [data, setData] = useState();
  const [reRender, setReRender] = useState(1);

  useEffect(() => {
    if (inData) {
      const newData = inData.map((el, idx, arr) => {
        let prvCollectionDays;
        if (idx + 1 < arr.length) {
          const d0 = new Date(arr[idx + 1].dateCollected).getTime();
          const d1 = new Date(arr[idx].dateCollected).getTime();
          prvCollectionDays = Math.floor((d1 - d0) / (1000 * 60 * 60 * 24));
        }
        return {
          ...el,
          prvCollectionDays,
        };
      });
      // Calculate Footer Values
      const avgWeight =
        newData.reduce((acc, value) => {
          return acc + value.collectionLines.reduce((a, v) => a + (v.netWeight ?? 0), 0);
        }, 0) / newData.length;
      const avgPrvCollectionDays =
        newData.reduce((acc, value) => {
          return acc + (value.prvCollectionDays ?? 0);
        }, 0) /
        (newData.length - 1);
      (newData as any).footer = { avgWeight, avgPrvCollectionDays };

      setData(newData);
      setReRender(reRender + 1);
    }
  }, [inData]);

  const rowClicked = useCallback(
    (row) => {
      history.push({
        pathname: generatePath(getRouteByName('Collection'), { id: row.id }),
        state: {
          // pathname: location.pathname,
          // ...location.state,
        },
      });
    },
    [history, location.pathname, location.state],
  );

  const columns = useMemo(
    () => [
      {
        id: 'code',
        header: t('Κωδικός Εντύπου'),
        columnName: t('Κωδικός Εντύπου'),
        accessorKey: 'code',
        orderByField: 'code',
        cell: TableCellV8,
        size: 20,
        footer: () => {
          return <div>{'Μέσες Τιμές'}</div>;
        },
      },
      {
        id: 'dateCollected',
        header: t('Ημερομηνία Συλλογής'),
        columnName: t('Ημερομηνία Συλλογής'),
        accessorKey: 'dateCollected',
        orderByField: 'dateCollected',
        cell: CellDateV8,
        size: 50,
        meta: { type: 'text' },
      },
      {
        id: 'prvCollectionDays',
        header: t('Ημέρες απο Προηγούμενη Συλλογή'),
        columnName: t('Ημέρες απο Προηγούμενη Συλλογή'),
        accessorKey: 'prvCollectionDays',
        // orderByField: 'prvCollectionDays',
        cell: ({ row }) => {
          return <div className="text-center">{row.original.prvCollectionDays}</div>;
        },
        size: 20,
        meta: { type: 'text' },
        footer: () => {
          const avgPrvCollectionDays =
            data && (data as any)?.footer ? (data as any)?.footer.avgPrvCollectionDays : undefined;

          return (
            <div className="text-center">
              {Number.isFinite(avgPrvCollectionDays) ? (
                <>{formatFloat(avgPrvCollectionDays, 2)}</>
              ) : (
                '-'
              )}
            </div>
          );
        },
      },
      {
        id: 'collectorId',
        header: t('Συλλέκτης'),
        columnName: t('Συλλέκτης'),
        accessorKey: 'collector.name',
        // orderByField: 'collector.name',
        cell: TableCellV8,
        size: 35,
        meta: { type: 'text' },
      },
      {
        id: 'wastereceptionpointId',
        header: t('Προορισμός'),
        columnName: t('Προορισμός'),
        accessorKey: 'wastereceptionpoint.name',
        // orderByField: 'wastereceptionpoint.name',
        cell: TableCellV8,
        size: 35,
        meta: { type: 'text' },
      },
      {
        id: 'collectionLines',
        header: t('Απόβλητα'),
        columnName: t('Απόβλητα'),
        accessorKey: 'collectionLines',
        // orderByField: 'collectionLines',
        cell: ({ getValue, row }) => {
          function compare(a, b) {
            if (a.aa < b.aa) {
              return -1;
            }
            if (a.aa > b.aa) {
              return 1;
            }
            return 0;
          }
          const lines = row.original.collectionLines;
          return (
            <div className="text-left">
              {lines.sort(compare).map((v, i) => {
                return (
                  <div key={i + v.aa}>
                    {v?.aa}: {v?.material?.name} ({v?.qty} ΤΕΜ) {v?.netWeight} kg
                  </div>
                );
              })}
            </div>
          );
        },
        size: 35,
        footer: () => {
          const avgWeight =
            data && (data as any)?.footer && !Number.isNaN((data as any)?.footer.avgWeight)
              ? (data as any)?.footer.avgWeight
              : 0;
          return (
            <div className="text-center">
              {avgWeight && <>{avgWeight ? formatFloat(avgWeight, 2) : 0} kg</>}
            </div>
          );
        },
      },
    ],
    [t, data],
  );

  const tableRef = useRef(null);
  // return of useRef
  useImperativeHandle(ref, () => (tableRef.current ? tableRef.current : null));

  return (
    <LoadingOverlay active={loading} spinner text={'Παρακαλώ περιμένετε...'}>
      {data ? (
        <TablePaginatedV8
          ref={tableRef}
          loading={loading}
          id="cp-stats-collections-grid"
          appStore={appStore}
          tableStore={tableStore}
          key={reRender}
          columns={columns as any}
          data={data}
          dataCount={dataCount}
          topBorderRadius={false}
          onRowClick={(table) => setSelected(table.row.original)}
          rowProps={(row: any) => {
            let style = undefined;
            if (row.original.id === selected?.id) style = gridRowSelectedStyle;
            if (row.original.isActive === false) style = { ...style, ...gridInactiveRowStyle };
            return { style: style };
          }}
          onExport={handleExport}
          onPageChange={onPageChange}
          initPageIndex={initPageIndex}
          initSorting={order2Sorting(initOrderBy)}
          onSortByChange={(sort) => {
            console.log('$$$-$ grid sortbychange', sort);
            // $$$
            const columnId = sort[0].id;
            const sortOrder = sort[0].desc ? 'DESC' : 'ASC';
            // find column by id
            const cols = columns.filter((r: any) => r.id === columnId);
            if (cols.length === 1) {
              const col = cols[0] as any;
              let order = col.orderByField ? col.orderByField : 'id';
              order = order + ' ' + sortOrder;
              onOrderChange(order);
            }
          }}
          showFooter={true}
          hidePagination={true}
        />
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </LoadingOverlay>
  );
});
